import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { api, API_VERSION_TWO } from 'apis/axiosInstance'
import { HTTPValidationError } from 'types/httpTypes'
import { ApiResponse, SavedSurveyQuestion } from './types'

type GetSurveyQuestionsResponse = ApiResponse<{
  survey_questions: Array<SavedSurveyQuestion>
  is_submitted: boolean
  is_closed: boolean
}>

/**
 * Fetches survey questions by survey ID from the API
 */
const getSurveyQuestions = async (surveyId: string): Promise<GetSurveyQuestionsResponse> => {
  return api.get(`/${API_VERSION_TWO}/common/surveys/${surveyId}/survey_questions`)
}

/**
 * Generates the query key for fetching survey questions
 */
export const getSurveyQuestionsQueryKey = (surveyId: string) => ['survey_questions', surveyId]

/**
 * @endpoint: GET: /v2/common/surveys/{surveyId}/survey_questions/
 * @summary This hook is used to get all survey questions by survey ID
 */
export const useGetSurveyQuestions = <
  TData = GetSurveyQuestionsResponse,
  TError = HTTPValidationError,
>(
  surveyId: string,
  opts?: {
    query?: UseQueryOptions<GetSurveyQuestionsResponse, TError, TData>
  },
) => {
  return useQuery({
    queryKey: getSurveyQuestionsQueryKey(surveyId),
    queryFn: () => getSurveyQuestions(surveyId),
    select: (response) => {
      return response?.data?.data ?? { survey_questions: [], is_submitted: false, is_closed: false }
    },
    enabled: opts?.query?.enabled,
  })
}
